import { motion } from "framer-motion";
import { Flag, Sparkle, ScissorsIcon } from "@phosphor-icons/react";

const HERITAGE_IMG =
  "https://images.pexels.com/photos/9726894/pexels-photo-9726894.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const stats = [
  { value: "100%", label: "Made in USA" },
  { value: "9", label: "American Ateliers" },
  { value: "48hr", label: "Hand-Tied Per Wig" },
  { value: "0", label: "Outsourced Steps" },
];

const pillars = [
  {
    icon: ScissorsIcon,
    title: "Atelier-Made",
    body: "Every wig is hand-ventilated, sewn, and finished in our American workshops — no overseas factories, no shortcuts.",
  },
  {
    icon: Sparkle,
    title: "Single-Origin Hair",
    body: "We trace each bundle to its donor. Ethically gathered, never blended, always honest.",
  },
  {
    icon: Flag,
    title: "American Craft",
    body: "Three generations of master stylists, working from heritage techniques refined right here at home.",
  },
];

export const Heritage = () => {
  return (
    <section
      id="heritage"
      data-testid="heritage-section"
      className="relative overflow-hidden"
    >
      <div className="absolute inset-0">
        <img
          src={HERITAGE_IMG}
          alt="Heritage texture"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 heritage-overlay" />
        <div className="absolute inset-0 grain pointer-events-none" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 py-24 sm:py-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9 }}
          className="max-w-3xl"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#E6CC98]" />
            <span className="text-xs uppercase tracking-[0.3em] gold-text font-body">
              Heritage
            </span>
          </div>
          <h2 className="font-heading font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#FDFBF7] leading-[1.05]">
            An American atelier,
            <br />
            <span className="italic gold-gradient-text">
              quietly redefining luxury.
            </span>
          </h2>
          <p className="mt-8 text-base sm:text-lg text-[#A39791] font-body leading-relaxed max-w-2xl">
            Kim Monroe LLC was born from a refusal — a refusal to cut corners,
            to dilute integrity, or to send our craft overseas. From sourcing
            to stitch, every step happens on American soil, by American hands.
          </p>
        </motion.div>

        {/* stats */}
        <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 border border-white/5">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, delay: i * 0.1 }}
              data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}
              className="bg-[#0a0705]/80 backdrop-blur-sm p-8 sm:p-10"
            >
              <div className="font-heading font-light text-4xl sm:text-5xl gold-gradient-text mb-2">
                {s.value}
              </div>
              <div className="text-xs uppercase tracking-[0.25em] text-[#A39791] font-body">
                {s.label}
              </div>
            </motion.div>
          ))}
        </div>

        {/* pillars */}
        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 border border-white/5">
          {pillars.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, delay: i * 0.12 }}
              data-testid={`pillar-${p.title.toLowerCase().replace(/\s/g, "-")}`}
              className="bg-[#0a0705]/80 backdrop-blur-sm p-10"
            >
              <p.icon size={32} weight="thin" className="text-[#E6CC98]" />
              <h3 className="font-heading text-2xl text-[#FDFBF7] mt-6 mb-3">
                {p.title}
              </h3>
              <p className="text-sm text-[#A39791] font-body leading-relaxed">
                {p.body}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
